import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { format } from 'date-fns';


const MaintenanceScheduler = ({ maintenance, courts }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showNewMaintenanceModal, setShowNewMaintenanceModal] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { 
        label: 'Agendado', 
        className: 'bg-primary/10 text-primary border-primary/20' 
      },
      in_progress: { 
        label: 'Em Andamento', 
        className: 'bg-warning/10 text-warning border-warning/20' 
      },
      completed: { 
        label: 'Concluído', 
        className: 'bg-success/10 text-success border-success/20' 
      },
      cancelled: { 
        label: 'Cancelado', 
        className: 'bg-destructive/10 text-destructive border-destructive/20' 
      }
    };
    
    const config = statusConfig?.[status] || statusConfig?.scheduled;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      high: { 
        label: 'Alta', 
        className: 'bg-destructive/10 text-destructive border-destructive/20' 
      },
      medium: { 
        label: 'Média', 
        className: 'bg-warning/10 text-warning border-warning/20' 
      },
      low: { 
        label: 'Baixa', 
        className: 'bg-success/10 text-success border-success/20' 
      }
    };
    
    const config = priorityConfig?.[priority] || priorityConfig?.medium;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const NewMaintenanceModal = ({ onClose, onSave }) => {
    const [formData, setFormData] = useState({
      courtId: '',
      type: '',
      description: '',
      scheduledDate: format(new Date(), 'yyyy-MM-dd'),
      estimatedDuration: 2,
      cost: 0,
      provider: '',
      priority: 'medium'
    });

    const handleInputChange = (field, value) => {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const handleSave = () => {
      onSave?.(formData);
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevation-4 max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-heading font-bold text-card-foreground">
                Agendar Manutenção
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                iconName="X"
                iconSize={16}
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Quadra
                </label>
                <Select
                  value={formData?.courtId}
                  onChange={(value) => handleInputChange('courtId', value)}
                >
                  <option value="">Selecione uma quadra</option>
                  {courts?.map((court) => (
                    <option key={court?.id} value={court?.id}>
                      {court?.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Tipo de Manutenção
                </label>
                <Input
                  type="text"
                  value={formData?.type}
                  onChange={(e) => handleInputChange('type', e?.target?.value)}
                  placeholder="Ex: Troca de grama sintética"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Descreva os detalhes da manutenção..."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Data Agendada
                  </label>
                  <Input
                    type="date"
                    value={formData?.scheduledDate}
                    onChange={(e) => handleInputChange('scheduledDate', e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Duração (horas)
                  </label>
                  <Input
                    type="number"
                    value={formData?.estimatedDuration}
                    onChange={(e) => handleInputChange('estimatedDuration', parseInt(e?.target?.value))}
                    min="1"
                    max="24"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Custo Estimado (R$)
                  </label>
                  <Input
                    type="number"
                    value={formData?.cost}
                    onChange={(e) => handleInputChange('cost', parseFloat(e?.target?.value))}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Prioridade
                  </label>
                  <Select
                    value={formData?.priority}
                    onChange={(value) => handleInputChange('priority', value)}
                  >
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Prestador de Serviço
                </label>
                <Input
                  type="text"
                  value={formData?.provider}
                  onChange={(e) => handleInputChange('provider', e?.target?.value)}
                  placeholder="Ex: Manutenção Sports Ltda"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button variant="default" onClick={handleSave}>
                  Agendar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const MaintenanceDetailModal = ({ maintenance, onClose }) => {
    if (!maintenance) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevation-4 max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-heading font-bold text-card-foreground">
                Detalhes da Manutenção
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                iconName="X"
                iconSize={16}
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Quadra</label>
                <div className="text-card-foreground font-semibold">{maintenance?.courtName}</div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">Tipo</label>
                <div className="text-card-foreground">{maintenance?.type}</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data</label>
                  <div className="text-card-foreground">
                    {new Date(maintenance?.scheduledDate)?.toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Duração</label>
                  <div className="text-card-foreground">{maintenance?.estimatedDuration}h</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="mt-1">{getStatusBadge(maintenance?.status)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Prioridade</label>
                  <div className="mt-1">{getPriorityBadge(maintenance?.priority)}</div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">Custo</label>
                <div className="text-lg font-semibold text-card-foreground">
                  R$ {maintenance?.cost?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">Prestador</label>
                <div className="text-card-foreground">{maintenance?.provider}</div>
              </div>

              <div className="flex space-x-3 pt-4">
                {maintenance?.status === 'scheduled' && (
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    iconName="Play"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Iniciar
                  </Button>
                )}
                
                {maintenance?.status === 'in_progress' && (
                  <Button
                    variant="success"
                    size="sm"
                    className="flex-1"
                    iconName="Check"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Concluir
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  iconName="MessageCircle"
                  iconPosition="left"
                  iconSize={16}
                >
                  Contato
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">
            Agendamento de Manutenção
          </h2>
          <p className="text-muted-foreground">
            Coordene manutenção com disponibilidade das quadras
          </p>
        </div>

        <Button
          variant="default"
          onClick={() => setShowNewMaintenanceModal(true)}
          iconName="Plus"
          iconPosition="left"
          iconSize={16}
        >
          Agendar Manutenção
        </Button>
      </div>
      {/* Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                {maintenance?.filter(m => m?.status === 'scheduled')?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Agendadas</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-warning/10 rounded-lg">
              <Icon name="Tool" size={20} className="text-warning" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                {maintenance?.filter(m => m?.status === 'in_progress')?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Em andamento</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-destructive/10 rounded-lg">
              <Icon name="AlertTriangle" size={20} className="text-destructive" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                {maintenance?.filter(m => m?.priority === 'high')?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Alta prioridade</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <Icon name="DollarSign" size={20} className="text-success" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                R$ {maintenance?.reduce((sum, m) => sum + (m?.cost || 0), 0)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <div className="text-sm text-muted-foreground">Custo total</div>
            </div>
          </div>
        </div>
      </div>
      {/* Maintenance List */}
      <div className="bg-card border border-border rounded-lg shadow-elevation-2">
        <div className="p-4 border-b border-border">
          <h3 className="font-heading font-semibold text-card-foreground">
            Manutenções Programadas
          </h3>
        </div>
        
        <div className="divide-y divide-border">
          {maintenance?.map((item) => (
            <div key={item?.id} className="p-4 hover:bg-muted/20 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-muted/30 rounded-lg">
                    <Icon name="Tool" size={16} className="text-muted-foreground" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-semibold text-card-foreground">
                        {item?.courtName}
                      </h4>
                      {getStatusBadge(item?.status)}
                      {getPriorityBadge(item?.priority)}
                    </div>
                    
                    <div className="text-sm text-muted-foreground mb-2">
                      {item?.type}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Data:</span>
                        <div className="font-medium text-card-foreground">
                          {new Date(item?.scheduledDate)?.toLocaleDateString('pt-BR')}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-muted-foreground">Duração:</span>
                        <div className="font-medium text-card-foreground">
                          {item?.estimatedDuration}h
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-muted-foreground">Custo:</span>
                        <div className="font-medium text-card-foreground">
                          R$ {item?.cost?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-muted-foreground">Prestador:</span>
                        <div className="font-medium text-card-foreground">
                          {item?.provider}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedMaintenance(item)}
                    iconName="Eye"
                    iconSize={14}
                  />
                  
                  {item?.status === 'scheduled' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Play"
                      iconSize={14}
                      className="text-warning hover:text-warning"
                    />
                  )}
                  
                  {item?.status === 'in_progress' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Check"
                      iconSize={14}
                      className="text-success hover:text-success"
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {maintenance?.length === 0 && (
          <div className="text-center py-12">
            <div className="flex items-center justify-center w-16 h-16 bg-muted/50 rounded-lg mx-auto mb-4">
              <Icon name="Tool" size={24} className="text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold text-card-foreground mb-2">
              Nenhuma manutenção agendada
            </h3>
            <p className="text-muted-foreground mb-4">
              Mantenha suas quadras em perfeito estado
            </p>
            <Button
              variant="default"
              onClick={() => setShowNewMaintenanceModal(true)}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}
            >
              Agendar Primeira Manutenção
            </Button>
          </div>
        )}
      </div>
      {/* Service Providers */}
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
        <h3 className="font-heading font-bold text-card-foreground mb-4">
          Prestadores de Serviço
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { name: 'Manutenção Sports Ltda', specialty: 'Grama Sintética', rating: 4.8, phone: '(11) 99999-0001' },
            { name: 'Elétrica Central', specialty: 'Iluminação', rating: 4.6, phone: '(11) 99999-0002' },
            { name: 'Reformas Quadras Pro', specialty: 'Pisos e Redes', rating: 4.9, phone: '(11) 99999-0003' }
          ]?.map((provider, index) => (
            <div key={index} className="p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-card-foreground">
                  {provider?.name}
                </h4>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-warning" />
                  <span className="text-sm font-medium">{provider?.rating}</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {provider?.specialty}
              </p>
              <Button
                variant="outline"
                size="sm"
                iconName="Phone"
                iconPosition="left"
                iconSize={14}
                className="w-full"
              >
                {provider?.phone}
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Modals */}
      <NewMaintenanceModal
        onClose={() => setShowNewMaintenanceModal(false)}
        onSave={(data) => {
          console.log('New maintenance:', data);
          setShowNewMaintenanceModal(false);
        }}
      />
      <MaintenanceDetailModal
        maintenance={selectedMaintenance}
        onClose={() => setSelectedMaintenance(null)}
      />
    </div>
  );
};

export default MaintenanceScheduler;